import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Link,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { patientService, Patient, PatientCreate } from '../services/patientService';
import patientTypeService, { PatientType } from '../services/patientTypeService';
import { clinicService } from '../services/clinicService';
import { openPatientScheduling } from '../services/schedulingLinkService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import EmailSendDialog from '../components/dialogs/EmailSendDialog';
import ComposeEmailDialog from '../components/communications/ComposeEmailDialog';
import SendQuestionnaireToPatientDialog from '../components/dialogs/SendQuestionnaireToPatientDialog';
import SmsSendDialog from '../components/dialogs/SmsSendDialog';
import { YomanSchedulingDialog } from '../components/dialogs/YomanSchedulingDialog';
import { PatientFormDialog } from '../components/dialogs/PatientFormDialog';
import { ViewMeetingsModal } from '../components/modals/ViewMeetingsModal';
import { FinishTreatmentModal } from '../components/modals/FinishTreatmentModal';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import TablePagination from '../components/table/TablePagination';
import useTablePage from '../components/table/hooks/useTablePage';
import { patientColumns } from '../config/patientColumns';
import { formatDateSafe as formatDate, formatDateTimeSafe as formatDateTime, calculateAge } from '../utils/dateUtils';
import { normalizePhone } from '../utils/phoneUtils';
import { getPatientStatusColor as getStatusColor } from '../utils/statusUtils';
import { usePatientStatusConfigs } from '../hooks/usePatientStatusConfigs';
import { usePatientFieldConfigs } from '../hooks/usePatientFieldConfigs';
import { RunQuestionnaireSummaryDialog } from '../components/dialogs/RunQuestionnaireSummaryDialog';


// Calculate age from date of birth
const TITRATION_MEASUREMENT_STATUS_LABELS: Record<string, string> = {
  valid: 'Valid',
  not_valid: 'Not Valid',
};

const TITRATION_MEASUREMENT_APPROVAL_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  approved: 'Approved',
  reject: 'Rejected',
  request_additional_info: 'Request Additional Info',
};

const MEDICATION_MONITORING_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  done: 'Done',
};

const PatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const { statusLabel, statusTitles } = usePatientStatusConfigs();
  const { fieldConfigs, dynamicColumns } = usePatientFieldConfigs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Entity-specific state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [idNumberServerError, setIdNumberServerError] = useState<string | null>(null);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [patientTypes, setPatientTypes] = useState<PatientType[]>([]);
  const [selectedPatientTypeId, setSelectedPatientTypeId] = useState<number | undefined>(undefined);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [yomanDialogOpen, setYomanDialogOpen] = useState(false);
  const [yomanLoading, setYomanLoading] = useState(false);
  const [viewMeetingsDialogOpen, setViewMeetingsDialogOpen] = useState(false);
  const [finishTreatmentDialogOpen, setFinishTreatmentDialogOpen] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [sendQuestionnaireDialogOpen, setSendQuestionnaireDialogOpen] = useState(false);
  const [runQSDialogOpen, setRunQSDialogOpen] = useState(false);
  const [runQSPatientId, setRunQSPatientId] = useState<number | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [searchInputValue, setSearchInputValue] = useState('');
  const [defaultCountryCode, setDefaultCountryCode] = useState('+44');
  const [clinicState, setClinicState] = useState<string | null>(null);
  const [clinics, setClinics] = useState<{ id: number; name: string }[]>([]);

  const { register, handleSubmit, reset, formState: { errors, submitCount }, control, watch } = useForm<PatientCreate & { status?: string; additional_fields?: Record<string, any> }>({ mode: 'onChange' });
  const watchedStatus = watch('status');

  const getSortValue = useCallback((patient: Patient, key: string): any => {
    switch (key) {
      case 'name': return patient.full_name.toLowerCase();
      case 'email': return patient.email?.toLowerCase() || '';
      case 'phone': return patient.phone || '';
      case 'age': return calculateAge(patient.date_of_birth) ?? -1;
      case 'status': return patient.status.toLowerCase();
      case 'lastMeeting': return patient.last_meeting_date || '';
      case 'nextMeeting': return patient.next_meeting_start || '';
      case 'idNumber': return patient.id_number?.toLowerCase() || '';
      case 'clinicName': return patient.clinic_name?.toLowerCase() || '';
      case 'referralName': return patient.referral_name?.toLowerCase() || '';
      case 'hmId': return patient.hm_id?.toLowerCase() || '';
      case 'gender': return patient.gender?.toLowerCase() || '';
      case 'startMedication': return patient.start_medication || '';

      case 'createdAt': return patient.created_at || '';
      case 'updatedAt': return patient.updated_at || '';
      case 'titrationStatus': return patient.titration_status?.toLowerCase() || '';
      case 'titrationMeasurementStatus': return patient.titration_measurement_status?.toLowerCase() || '';
      case 'titrationMeasurementApprovalStatus': return patient.titration_measurement_approval_status?.toLowerCase() || '';
      case 'medicationMonitoringStatus': return patient.medication_monitoring_status?.toLowerCase() || '';
      case 'treatmentStatus': return patient.treatment_status?.toLowerCase() || '';
      case 'nextMeetingType': return patient.next_meeting_type_name?.toLowerCase() || '';
      case 'finishReason': return patient.finish_reason?.toLowerCase() || '';
      case 'isTest': return patient.is_test ? '1' : '0';
      default:
        if (key.startsWith('df_')) {
          const fieldKey = key.slice(3);
          const value = patient.additional_fields?.[fieldKey];
          return value ?? '';
        }
        return '';
    }
  }, []);

  const idNumberLabel = clinicState === 'UK' ? 'NHS Number' : 'ID Number';
  const allColumns = React.useMemo(
    () => [
      ...patientColumns.map((col) =>
        col.key === 'idNumber' ? { ...col, label: idNumberLabel } : col
      ),
      ...dynamicColumns,
    ],
    [dynamicColumns, idNumberLabel]
  );

  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;

  const table = useTablePage<Patient>({
    entityType: 'patients',
    columns: allColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    filters,
    onFiltersChange: setFilters,
  });

  // Reset to page 1 when filters, sort, search, or tenant change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.status, filters.clinic_id, currentTenant?.id, table.sortBy, table.sortOrder, table.searchQuery]);

  // Load patients data (server-side pagination)
  useEffect(() => {
    const loadPatients = async () => {
      if (!user || !currentTenant) {
        table.setLoading(false);
        return;
      }
      try {
        table.setLoading(true);
        const skip = (currentPage - 1) * PAGE_SIZE;
        const statusFilters: string[] = filters.status || [];
        const clinicFilters: number[] = filters.clinic_id || [];
        const result = await patientService.getPatients(currentTenant.id, statusFilters, clinicFilters, skip, PAGE_SIZE, table.sortBy ?? undefined, table.sortOrder, table.searchQuery || undefined);
        setTotalCount(result.total);
        table.setData(result.items);
        table.setError(null);
      } catch (err) {
        table.setError(err instanceof Error ? err.message : 'Unknown error');
        toast.error('Failed to load patients');
      } finally {
        table.setLoading(false);
      }
    };
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentTenant, refreshKey, localRefreshKey, currentPage, filters.status, filters.clinic_id, table.sortBy, table.sortOrder, table.searchQuery]);

  // Load patient types
  useEffect(() => {
    if (!currentTenant) return;
    patientTypeService.getAll(currentTenant.id).then(setPatientTypes).catch(() => {});
  }, [currentTenant]);

  // Load clinics
  useEffect(() => {
    if (!currentTenant) return;
    clinicService.getClinics(currentTenant.id).then((data) => {
      setClinics(data.map((c) => ({ id: c.id, name: c.name })));
      if (data.length > 0) {
        if (data[0].default_country_code) setDefaultCountryCode(data[0].default_country_code);
        setClinicState(data[0].state || null);
      }
    }).catch(() => {});
  }, [currentTenant, refreshKey]);

  // Load meeting types for Yoman scheduling
  useEffect(() => {
    const loadMeetingTypes = async () => {
      if (!currentTenant) {
        setMeetingTypes([]);
        return;
      }

      try {
        const types = await meetingTypeService.getMeetingTypes(currentTenant.id);
        setMeetingTypes(types);
      } catch (err) {
        console.error('Error loading meeting types:', err);
        setMeetingTypes([]);
      }
    };

    loadMeetingTypes();
  }, [currentTenant]);

  // Entity-specific handlers
  const handleViewDetails = () => {
    if (table.selectedItem) {
      navigate(`/patients/${table.selectedItem.id}`);
    }
    table.handleMenuClose();
  };

  const handleEdit = () => {
    if (table.selectedItem) {
      setEditingPatient(table.selectedItem);
      setSelectedPatientTypeId(table.selectedItem.patient_type_id ?? undefined);
      reset({
        first_name: table.selectedItem.first_name,
        last_name: table.selectedItem.last_name,
        email: table.selectedItem.email || '',
        phone: table.selectedItem.phone || '',
        date_of_birth: table.selectedItem.date_of_birth,
        start_medication: table.selectedItem.start_medication || '',
        status: table.selectedItem.status,
        id_number: table.selectedItem.id_number || '',
        gender: table.selectedItem.gender || '',
        additional_fields: table.selectedItem.additional_fields || {},
      });
      setAddDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleViewMeetings = () => {
    if (table.selectedItem) {
      setViewMeetingsDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleScheduleYomanMeeting = () => {
    if (!table.selectedItem) return;
    setYomanDialogOpen(true);
    // Clear menu position but keep selectedItem for the dialog
    table.handleMenuClose();
  };

  const handleYomanCreateInvite = async (meetingTypeId: number, therapistId?: number) => {
    if (!dialogPatient) {
      return;
    }

    try {
      setYomanLoading(true);

      // The in-app scheduling page, which offers this clinic's own
      // availability - no round trip to generate a link.
      // The clinician already chose both in the dialog; the page
      // honours them instead of working it out from patient state.
      if (!openPatientScheduling(dialogPatient.uuid, {
        meetingTypeId, therapistId,
        // Always the booking screen: this dialog exists to arrange a
        // NEW meeting, including when the patient already has one.
        book: true,
      }, dialogPatient.id)) {
        toast.error('This patient has no scheduling link yet.');
        return;
      }

      toast.success('Opening scheduling for this patient');
      setYomanDialogOpen(false);
    } catch (error) {
      console.error('Error opening scheduling:', error);
      toast.error('Could not open scheduling for this patient');
      throw error;
    } finally {
      setYomanLoading(false);
    }
  };

  const handleYomanDialogClose = () => {
    setYomanDialogOpen(false);
    setYomanLoading(false);
  };

  const handleSendEmail = () => {
    if (table.selectedItem) {
      setEmailDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleComposeEmail = () => {
    if (table.selectedItem) {
      setComposeOpen(true);
    }
    table.handleMenuClose();
  };

  const handleSendSms = () => {
    if (table.selectedItem) {
      setSmsDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleSendQuestionnaireEmail = () => {
    if (!table.selectedItem) return;
    table.handleMenuClose();
    setSendQuestionnaireDialogOpen(true);
  };

  const handleRunQuestionnaireSummary = () => {
    if (!table.selectedItem) return;
    setRunQSPatientId(table.selectedItem.id);
    setRunQSDialogOpen(true);
    table.handleMenuClose();
  };

  const handleFinish = () => {
    if (!table.selectedItem) return;
    setFinishTreatmentDialogOpen(true);
    table.handleMenuClose();
    table.handleStatusMenuClose();
  };

  const handleFinishSuccess = () => {
    if (dialogPatient) {
      toast.success('Patient treatment finished successfully');
      table.setData(table.data.map(p =>
        p.id === dialogPatient.id ? { ...p, status: 'finish' as const } : p
      ));
    }
  };

  const handleRevive = async () => {
    if (!table.selectedItem) return;
    const patientId = table.selectedItem.id;

    try {
      await patientService.revivePatient(patientId);
      toast.success('Patient revived successfully');
      table.setData(table.data.map(p =>
        p.id === patientId ? { ...p, status: 'new' as const } : p
      ));
    } catch (error) {
      console.error('Error reviving patient:', error);
      toast.error('Failed to revive patient');
    }
    table.handleStatusMenuClose();
  };

  const onSubmit = async (data: PatientCreate) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    try {
      const { additional_fields, ...restData } = data as any;

      // Convert form values back to correct types before saving (bool strings → boolean)
      const convertedAdditionalFields: Record<string, any> = { ...(additional_fields || {}) };
      for (const cfg of fieldConfigs) {
        const val = convertedAdditionalFields[cfg.key];
        if (cfg.field_type === 'bool' && (val === 'true' || val === 'false')) {
          convertedAdditionalFields[cfg.key] = val === 'true';
        } else if (cfg.field_type === 'number' && val !== undefined && val !== '') {
          convertedAdditionalFields[cfg.key] = Number(val);
        }
      }

      const patientData = {
        ...restData,
        tenant_id: currentTenant.id,
        phone: normalizePhone(data.phone, defaultCountryCode),
        start_medication: (restData as any).start_medication || null,
        patient_type_id: selectedPatientTypeId ?? null,
        additional_fields: Object.keys(convertedAdditionalFields).length > 0 ? convertedAdditionalFields : undefined,
      };

      if (editingPatient) {
        const updatedPatient = await patientService.updatePatient(editingPatient.id, patientData);
        table.setData(table.data.map(p => p.id === editingPatient.id ? updatedPatient : p));
        toast.success('Patient updated successfully');
      } else {
        await patientService.createPatient(patientData);
        toast.success('Patient created successfully');
        setLocalRefreshKey(k => k + 1);
      }

      setAddDialogOpen(false);
      setEditingPatient(null);
      reset();
    } catch (error: any) {
      console.error('Error saving patient:', error);
      console.log('Error response data:', JSON.stringify(error.response?.data));
      console.log('Error response status:', error.response?.status);
      const detail = error.response?.data?.detail;
      const fullError = JSON.stringify(error.response?.data || '').toLowerCase();
      if (error.response?.status === 400 && (fullError.includes('id number') || fullError.includes('id_number'))) {
        setIdNumberServerError('ID Number / NHS Number already exists');
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Failed to save patient');
      }
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];

    const options: ContextMenuOption[] = [
      {
        label: 'Edit',
        icon: '✏️',
        onClick: handleEdit,
      },
      {
        label: 'Create Meeting',
        icon: '📆',
        onClick: handleScheduleYomanMeeting,
      },
      {
        // The whole chain the patient is due, on their own scheduling page -
        // as opposed to Create Meeting above, which arranges one appointment
        // of a chosen type.
        label: 'Open Scheduling Page',
        icon: '🗓️',
        onClick: () => {
          if (!openPatientScheduling(table.selectedItem?.uuid, undefined,
                                     table.selectedItem?.id)) {
            toast.error('This patient has no scheduling link yet.');
          }
        },
      },
      {
        label: 'View Meetings',
        icon: '📋',
        onClick: handleViewMeetings,
      },
      {
        label: 'View Patient',
        icon: '👤',
        onClick: handleViewDetails,
      },
    ];

    if (table.selectedItem.status !== 'finish') {
      options.push({
        label: 'Finish Treatment',
        icon: (
          <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
          </svg>
        ),
        onClick: handleFinish,
      });
    }

    options.push(
      {
        label: 'Send Email Template',
        icon: '📧',
        onClick: handleSendEmail,
      },
      {
        label: 'Send Email',
        icon: '✉️',
        onClick: handleComposeEmail,
        disabled: !table.selectedItem?.email,
      },
      {
        label: 'Send SMS',
        icon: '📱',
        onClick: handleSendSms,
      },
      {
        label: 'Send Questionnaire Email',
        icon: '📋',
        onClick: handleSendQuestionnaireEmail,
      }
    );

    if (user?.user_privilege?.prompt_development) {
      options.push({
        label: 'Run Questionnaire Summary',
        icon: '🤖',
        onClick: handleRunQuestionnaireSummary,
      });
    }

    return options;
  };

  // Column value renderer
  const getColumnValue = (patient: Patient, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/patients/${patient.id}`);
              }}
              sx={{ textAlign: 'left' }}
            >
              {patient.full_name}
            </Link>
          </Box>
        );
      case 'email': return patient.email || '—';
      case 'phone': return patient.phone || '—';
      case 'age': {
        const age = calculateAge(patient.date_of_birth);
        return age === null ? '—' : `${age} years`;
      }
      case 'status':
        return (
          <Chip
            label={statusLabel(patient.status)}
            color={getStatusColor(patient.status) as any}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              table.handleStatusClick(e, patient);
            }}
            sx={{ cursor: 'pointer' }}
          />
        );
      case 'lastMeeting': return formatDate(patient.last_meeting_date);
      case 'nextMeeting': return formatDate(patient.next_meeting_start);
      case 'idNumber': return patient.id_number || '—';
      case 'clinicName': return patient.clinic_name || '—';
      case 'referralName': return patient.referral_name || '—';
      case 'hmId': return patient.hm_id || '—';
      case 'gender': return patient.gender || '—';
      case 'startMedication': return patient.start_medication ? formatDate(patient.start_medication) : '—';
      case 'createdAt': return formatDateTime(patient.created_at);
      case 'updatedAt': return formatDateTime(patient.updated_at);
      case 'titrationStatus': return patient.titration_status || '—';
      case 'titrationMeasurementStatus': return TITRATION_MEASUREMENT_STATUS_LABELS[patient.titration_measurement_status || ''] || patient.titration_measurement_status || '—';
      case 'titrationMeasurementApprovalStatus': return TITRATION_MEASUREMENT_APPROVAL_STATUS_LABELS[patient.titration_measurement_approval_status || ''] || patient.titration_measurement_approval_status || '—';
      case 'medicationMonitoringStatus': return MEDICATION_MONITORING_STATUS_LABELS[patient.medication_monitoring_status || ''] || patient.medication_monitoring_status || '—';
      case 'treatmentStatus': return patient.treatment_status || '—';
      case 'doctor': return patient.doctor_name || '—';
      case 'caseManager': return patient.case_manager_name || '—';
      case 'nursePrescriber': return patient.nurse_prescriber_name || '—';
      case 'nextMeetingType': return patient.next_meeting_type_name || '—';
      case 'finishReason': return patient.finish_reason || '—';
      case 'finishNotes': return patient.finish_notes ? patient.finish_notes.substring(0, 50) + (patient.finish_notes.length > 50 ? '...' : '') : '—';
      case 'isTest': return patient.is_test ? 'Yes' : 'No';
      default:
        if (columnKey.startsWith('df_')) {
          const fieldKey = columnKey.slice(3);
          const value = patient.additional_fields?.[fieldKey];
          if (value === null || value === undefined) return '—';
          const config = fieldConfigs.find(c => c.key === fieldKey);
          if (config?.field_type === 'bool') return value ? 'Yes' : 'No';
          if (config?.field_type === 'date') return formatDate(value);
          if (config?.field_type === 'single_select' && config.options) {
            const opt = config.options.find(o => o.value === value);
            return opt ? opt.label : String(value);
          }
          return String(value);
        }
        return '—';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (patient: Patient) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center" flex={1}>
          <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
          <Link
            component="button"
            variant="h6"
            onClick={() => navigate(`/patients/${patient.id}`)}
            sx={{ textAlign: 'left', fontWeight: 'medium', fontSize: '1.1rem' }}
          >
            {patient.full_name}
          </Link>
        </Box>
        <Chip
          label={statusLabel(patient.status)}
          color={getStatusColor(patient.status) as any}
          size="small"
          onClick={(e) => table.handleStatusClick(e, patient)}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
      <Box sx={{ pl: 4 }}>
        {patient.email && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            📧 {patient.email}
          </Typography>
        )}
        {patient.phone && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            📱 {patient.phone}
          </Typography>
        )}
        {calculateAge(patient.date_of_birth) !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            🎂 {calculateAge(patient.date_of_birth)} years old
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          📅 Last meeting: {formatDate(patient.last_meeting_date)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          📆 Next meeting: {formatDate(patient.next_meeting_start)}
        </Typography>
      </Box>
    </>
  );

  // We need to keep selectedItem alive for dialogs even after menu closes
  // Store the patient for Yoman/Email dialogs separately
  const [dialogPatient, setDialogPatient] = useState<Patient | null>(null);

  // Update dialogPatient whenever selectedItem changes (and is non-null)
  useEffect(() => {
    if (table.selectedItem) {
      setDialogPatient(table.selectedItem);
    }
  }, [table.selectedItem]);

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading patients:">
      <DataTableToolbar
        {...table.toolbarProps}
        searchQuery={searchInputValue}
        onSearchChange={setSearchInputValue}
        onSearchSubmit={() => { table.setSearchQuery(searchInputValue); setCurrentPage(1); }}
        title="Patients"
        searchPlaceholder="Search by name, email, phone, ID number, or HM ID..."
        isAdmin={!!user?.user_privilege?.config}
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Status Filter</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                multiple
                value={filters.status || []}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    status: typeof value === 'string' ? value.split(',') : value,
                  }));
                }}
                input={<OutlinedInput label="Status Filter" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={statusLabel(value)} size="small" />
                    ))}
                  </Box>
                )}
              >
                {Object.entries(statusTitles).map(([key, title]) => (
                  <MenuItem key={key} value={key}>
                    <Checkbox checked={(filters.status || []).includes(key)} />
                    <ListItemText primary={title} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {clinics.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="clinic-filter-label">Clinic</InputLabel>
                <Select
                  labelId="clinic-filter-label"
                  multiple
                  value={filters.clinic_id || []}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      clinic_id: typeof value === 'string' ? value.split(',').map(Number) : value,
                    }));
                  }}
                  input={<OutlinedInput label="Clinic" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => (
                        <Chip key={id} label={clinics.find((c) => c.id === id)?.name ?? id} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {clinics.map((clinic) => (
                    <MenuItem key={clinic.id} value={clinic.id}>
                      <Checkbox checked={(filters.clinic_id || []).includes(clinic.id)} />
                      <ListItemText primary={clinic.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(p) => p.id}
          emptyIcon={<PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No patients found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick}
            getRowKey={(p) => p.id}
          />
        </DataTableContainer>
      )}

      {showPagination && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => setCurrentPage(p => Math.max(1, p - 1))}
          onNext={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
        />
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {runQSPatientId && (
        <RunQuestionnaireSummaryDialog
          open={runQSDialogOpen}
          onClose={() => { setRunQSDialogOpen(false); setRunQSPatientId(null); }}
          patientId={runQSPatientId}
        />
      )}

      {/* Status Menu */}
      <Menu
        anchorEl={table.statusAnchorEl}
        open={Boolean(table.statusAnchorEl)}
        onClose={table.handleStatusMenuClose}
      >
        {table.selectedItem?.status !== 'finish' ? (
          <MenuItem onClick={handleFinish}>
            Finish Treatment
          </MenuItem>
        ) : (
          <MenuItem onClick={handleRevive}>
            Revive Patient
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Patient Dialog */}
      <PatientFormDialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); setIdNumberServerError(null); setSelectedPatientTypeId(undefined); }}
        onSubmit={handleSubmit(onSubmit)}
        editingPatient={editingPatient}
        register={register}
        errors={errors}
        submitCount={submitCount}
        control={control}
        defaultCountryCode={defaultCountryCode}
        clinicState={clinicState}
        fieldErrors={idNumberServerError ? { id_number: idNumberServerError } : {}}
        onClearFieldError={() => setIdNumberServerError(null)}
        dynamicFieldConfigs={fieldConfigs.filter(c => c.display_in_edit)}
        watchStatus={watchedStatus}
        patientTypeId={selectedPatientTypeId}
        patientTypes={patientTypes}
        onPatientTypeChange={setSelectedPatientTypeId}
      />

      {/* Email Send Dialog */}
      {dialogPatient && (
        <EmailSendDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          patientId={dialogPatient.id}
          patientName={dialogPatient.full_name}
          patientEmail={dialogPatient.email}
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}

      {/* Free-text Compose Email Dialog */}
      {dialogPatient && (
        <ComposeEmailDialog
          open={composeOpen}
          onClose={() => setComposeOpen(false)}
          patientId={dialogPatient.id}
          patientName={dialogPatient.full_name}
          patientEmail={dialogPatient.email ?? null}
          onSent={() => { setComposeOpen(false); toast.success('Email sent'); }}
        />
      )}

      {/* SMS Send Dialog */}
      {dialogPatient && (
        <SmsSendDialog
          open={smsDialogOpen}
          onClose={() => setSmsDialogOpen(false)}
          patientId={dialogPatient.id}
          patientName={dialogPatient.full_name}
          patientPhone={dialogPatient.phone}
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}

      {/* Yoman Scheduling Dialog */}
      <YomanSchedulingDialog
        open={yomanDialogOpen}
        onClose={handleYomanDialogClose}
        patient={dialogPatient}
        meetingTypes={meetingTypes}
        loading={yomanLoading}
        currentTenantId={currentTenant?.id}
        onCreateInvite={handleYomanCreateInvite}
      />

      {/* View Meetings Modal */}
      {dialogPatient && (
        <ViewMeetingsModal
          isOpen={viewMeetingsDialogOpen}
          onClose={() => setViewMeetingsDialogOpen(false)}
          patientId={dialogPatient.id}
          patientName={dialogPatient.full_name}
        />
      )}

      {/* Finish Treatment Modal */}
      {dialogPatient && (
        <FinishTreatmentModal
          isOpen={finishTreatmentDialogOpen}
          onClose={() => setFinishTreatmentDialogOpen(false)}
          patientId={dialogPatient.id}
          patientName={dialogPatient.full_name}
          onSuccess={handleFinishSuccess}
        />
      )}

      {/* Send Questionnaire to Patient Dialog */}
      {dialogPatient && currentTenant && (
        <SendQuestionnaireToPatientDialog
          open={sendQuestionnaireDialogOpen}
          onClose={() => setSendQuestionnaireDialogOpen(false)}
          patientId={dialogPatient.id}
          patientName={dialogPatient.full_name}
          tenantId={currentTenant.id}
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default PatientsPage;
